import styled, { keyframes, css } from '../../../styles/styled';
import heroImage from '../../../assets/Chart.png';
import { ButtonFilled } from '../../../components/ui/Button';
import { CancelButton } from '../../../components/WalletEntry/styled';

/**
 * PaymentQR Screen Styled Components
 * Task 9.1: Figma-aligned styles with dark cyber-luxury aesthetic
 * Refactored to use theme.palette for consistency
 */

// Animation keyframes
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.6;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

// Main screen container
export const ScreenContainer = styled.main`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: linear-gradient(180deg, ${({ theme }) => theme.palette.dark} 0%, ${({ theme }) => theme.palette.darkMedium} 100%);
  color: ${({ theme }) => theme.palette.white};
  padding: ${({ theme }) => theme.dimensions.spacingBase16}px;
  animation: ${fadeIn} 0.3s ease-out;

  @media ${({ theme }) => theme.devices.medium} {
    padding: ${({ theme }) => theme.dimensions.basePadding}px;
  }

  @media ${({ theme }) => theme.devices.large} {
    padding: 32px 48px;
    max-width: ${({ theme }) => theme.dimensions.contentMaxWidthBig}px;
    margin: 0 auto;
  }
`;


// Layout components matching WalletAddressEntry
export const GradientSection = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 32px 24px;
  background: linear-gradient(180deg, #15191E 0%, #000000 15%, #000000 33%, #15191E 100%);
  border-radius: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 720px) {
    padding: 24px 16px;
    border-radius: 0 16px 16px 16px;
    gap: 16px;
  }
`;

export const HeaderStack = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
`;

// Content wrapper (replacing previous ContentWrapper for consistency)
export const Content = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: 720px) {
    gap: 16px;
  }
`;

export const ScreenTitle = styled.h1`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

// Content wrapper
export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  flex: 1;

  @media (min-width: 768px) {
    gap: 32px;
  }
`;

// Loading skeleton styles
export const LoadingSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.dimensions.spacingBase16}px;
  padding: ${({ theme }) => theme.dimensions.basePadding}px;
  background: ${({ theme }) => theme.palette.darkMedium};
  border-radius: 16px;
  border: 1px solid rgba(82, 211, 129, 0.15);
`;

export const SkeletonLine = styled.div<{ $width?: string; $height?: string }>`
  width: ${({ $width }) => $width || '100%'};
  height: ${({ $height }) => $height || '20px'};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.darkMedium} 0%,
    rgba(82, 211, 129, 0.1) 50%,
    ${({ theme }) => theme.palette.darkMedium} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 8px;
`;

export const SkeletonQR = styled.div`
  width: 200px;
  height: 200px;
  margin: ${({ theme }) => theme.dimensions.basePadding}px auto;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.palette.darkMedium} 0%,
    rgba(82, 211, 129, 0.1) 50%,
    ${({ theme }) => theme.palette.darkMedium} 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 12px;
`;

// Status container with live region
export const StatusContainer = styled.div<{ $status?: string }>`
  position: relative;
  transition: opacity 0.3s ease-out;

  ${({ $status, theme }) =>
    $status === 'PENDING' &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: -2px;
        left: -2px;
        right: -2px;
        bottom: -2px;
        background: rgba(82, 211, 129, 0.4);
        border-radius: 18px;
        animation: ${pulse} 2s infinite;
        z-index: -1;
      }
    `}
`;

// Card container
export const Card = styled.div`
  background: ${({ theme }) => theme.palette.darkMedium};
  border-radius: 16px;
  border: 1px solid rgba(82, 211, 129, 0.15);
  padding: ${({ theme }) => theme.dimensions.basePadding}px;
  animation: ${fadeIn} 0.3s ease-out;

  @media ${({ theme }) => theme.devices.medium} {
    padding: 32px;
  }
`;

// Back button
export const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px ${({ theme }) => theme.dimensions.spacingBase16}px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.palette.white};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  transition: ${({ theme }) => theme.ui.transition('color')};

  &:hover {
    color: ${({ theme }) => theme.palette.primary};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;

// Error banner
export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px ${({ theme }) => theme.dimensions.spacingBase16}px;
  background: rgba(255, 103, 130, 0.1);
  border: 1px solid rgba(255, 103, 130, 0.3);
  border-radius: 8px;
  color: ${({ theme }) => theme.palette.error};
  font-size: ${({ theme }) => theme.fontSize.mediumBase};
`;

/**
 * Figma-aligned components (node-id: 24-18)
 * Colors from Figma variables:
 * - Graphit: #15191E (theme.palette.darkMedium)
 * - Green - trading: #52D381 (theme.palette.primary)
 * - #EFEFEF (theme.palette.white)
 * - Dark Dark grey: #292B2E
 * - Timer background: #343434
 */

// Timer bar (Figma: Group 48317)
export const TimerBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 12px;
  background: #343434;
  border-radius: 8px;
  font-family: 'Roboto', sans-serif;
`;

export const TimerLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.mediumBase};
  font-weight: ${({ theme }) => theme.fontWeight.light};
  color: ${({ theme }) => theme.palette.white};
  line-height: 14px;
`;

export const TimerValue = styled.span<{ $isWarning?: boolean; $isCritical?: boolean }>`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ $isWarning, $isCritical, theme }) =>
    $isCritical
      ? theme.palette.error
      : $isWarning
      ? theme.palette.warning
      : theme.palette.white};
  line-height: 20px;
`;

// Progress steps (Figma: Group 48328)
export const ProgressSteps = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  padding: 0 24px;
  margin-bottom: 16px;
`;

export const ProgressLine = styled.div`
  position: absolute;
  top: 50%;
  left: 48px;
  right: 48px;
  height: 1px;
  background: ${({ theme }) => theme.palette.gray};
  transform: translateY(-50%);
`;

export const ProgressStep = styled.div<{ $active?: boolean; $completed?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  z-index: 1;
`;

export const ProgressDot = styled.div<{ $active?: boolean; $completed?: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ $active, $completed, theme }) =>
    $active || $completed ? theme.palette.primary : theme.palette.gray};
`;

export const ProgressLabel = styled.span<{ $active?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.label};
  color: ${({ $active, theme }) =>
    $active ? theme.palette.primary : theme.palette.grayMedium};
  white-space: nowrap;
`;

/**
 * Payment hero title — DS h2 with bull-green emphasis on the amount.
 * Per design `to-amount-hero` (28/700, letter-spacing -0.005em).
 */
export const PaymentTitle = styled.h2`
  font-family: 'Roboto', sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: #EFEFEF;
  text-align: center;
  line-height: 1.2;
  letter-spacing: -0.005em;
  margin: 0 0 8px;
  white-space: pre-wrap;

  span {
    color: #52D381;
    font-variant-numeric: tabular-nums;
  }

  @media (max-width: 720px) {
    font-size: 22px;
  }
`;

/**
 * Legacy network info — used by the non-wallet-assignment payment path.
 * The new wallet-assignment flow uses PaymentSubtitle below.
 */
export const NetworkInfo = styled.div`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 300;
  color: #efefef;
  text-align: center;
  line-height: 18px;
  white-space: pre-wrap;

  strong {
    font-weight: 700;
  }

  span.regular {
    font-weight: 400;
  }
`;

/**
 * Subtitle below the hero. Per design `to-sub`: 14/300 / fg-2 with
 * Trading Battles bold and the network name tinted bull green.
 */
export const PaymentSubtitle = styled.p`
  font-size: 14px;
  font-weight: 300;
  color: #A7A8B2;
  text-align: center;
  margin: 0 auto 28px;
  max-width: 520px;
  line-height: 1.5;

  strong {
    color: #EFEFEF;
    font-weight: 700;
  }

  em {
    color: #52D381;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0.04em;
  }
`;

/**
 * Bull-green QR frame per design `qr-frame`. Hosts the dark QR on bull
 * background plus the SEND HERE arrow chip that floats on the top edge.
 */
export const QRFrame = styled.div`
  position: relative;
  padding: 16px;
  background: #52D381;
  border-radius: 14px;
  margin: 0 auto 20px;
  width: 220px;
  height: 220px;
  box-shadow: 0 0 0 1px rgba(82, 211, 129, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    display: block;
    width: 188px;
    height: 188px;
  }
`;

export const QRArrowIn = styled.span`
  position: absolute;
  top: -14px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 8px;
  background: #52D381;
  color: #0A0A0A;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.16em;
  border: 2px solid #15191E;
  white-space: nowrap;

  svg {
    width: 11px;
    height: 11px;
  }
`;

// QR container — outer flex wrapper kept for consumer compatibility.
export const QRContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 24px 0 12px;
`;

/**
 * Address block — label + mono address + reveal/copy actions.
 * Per design `addr-block`.
 */
/**
 * AddressSection — DS-4 deposit-surface from
 * trading-battles-design-system 4/project/preview/components-deposit-surface.html
 *
 * The container is now a tinted bull surface tile (bg #15191E + 1 px
 * recipient-focus-ring border) instead of the previous flat column.
 * Address text-row uses Roboto Mono per spec; the mono content lives
 * directly inside the surface, with the accent label inlined as a
 * prefix and an optional meta sub-line beneath.
 */
export const AddressSection = styled.div`
  width: 100%;
  max-width: 460px;
  margin: 0 auto 12px;
  background: #15191E;
  border: 1px solid rgba(82, 211, 129, 0.18);
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  text-align: center;
`;

/**
 * Inline "Deposit address ·" accent prefix — bull green / 700 — lives
 * on the same row as the address itself, separated by a middot.
 */
export const AddressLabel = styled.span`
  font-family: 'Roboto Mono', ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 13px;
  font-weight: 700;
  color: #52D381;
  user-select: none;
  letter-spacing: 0.01em;
`;

export const AddressValue = styled.span`
  font-family: 'Roboto Mono', ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 13px;
  font-weight: 400;
  color: #A7A8B2;
  letter-spacing: 0.01em;
  word-break: break-all;
  user-select: all;
  line-height: 1.45;
`;

/**
 * Address row wrapper — keeps the accent label and address on the
 * same flowing line so they read as a single sentence ("Deposit
 * address · 0xAB…2A4") rather than two stacked rows.
 */
export const AddressRow = styled.div`
  font-family: 'Roboto Mono', ui-monospace, 'SF Mono', Menlo, monospace;
  font-size: 13px;
  line-height: 1.45;
  text-align: center;
  word-break: break-all;
`;

/**
 * Meta sub-line beneath the address — "{network} · send exactly
 * {amount} {currency}" cue. 11 px / fg-3, no letter-spacing,
 * non-selectable so users can keep dragging the address itself.
 */
export const AddressMeta = styled.span`
  display: block;
  margin-top: 4px;
  font-family: 'Roboto', sans-serif;
  font-size: 11px;
  font-weight: 400;
  color: #7A7E8C;
  letter-spacing: 0;
  user-select: none;
  text-align: center;
`;

export const AddressActions = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  align-self: center;
`;

/**
 * Reveal toggle and Copy button now share the DS-4 tertiary-compact
 * spec from components-button-tertiary-compact.html (38 h, transparent
 * fill, 1 px #696969 border, #EFEFEF / 13 / 500). Dropping the previous
 * bull-outlined Copy variant collapses the visual hierarchy of the
 * two address actions — both are now neutral siblings, with the
 * Copy success state still flashing the bull-green check inline.
 */
export const RevealButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  background: transparent;
  border: 1px solid #696969;
  border-radius: 10px;
  color: #EFEFEF;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 200ms ease-in, border-color 200ms ease-in;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.04);
    border-color: #8A8A8A;
  }

  &:focus-visible {
    outline: 2px solid #52D381;
    outline-offset: 2px;
  }

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

/**
 * Copy button — sibling of RevealButton. Same DS-4 tertiary-compact
 * spec; the success state still tints text bull-green for the brief
 * "Copied!" flash.
 */
export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 18px;
  background: transparent;
  border: 1px solid #696969;
  border-radius: 10px;
  color: #EFEFEF;
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 200ms ease-in, border-color 200ms ease-in, color 200ms ease-in;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.04);
    border-color: #8A8A8A;
  }

  &:focus-visible {
    outline: 2px solid #52D381;
    outline-offset: 2px;
  }

  svg {
    flex-shrink: 0;
  }
`;

/**
 * UnderpaidTitle — DS-2 Detected/under H1. 26/700 with bear-coloured
 * span for the "but the amount is insufficient" emphasis. Mobile drops
 * to 22 to keep the title from wrapping into too many lines.
 */
export const UnderpaidTitle = styled.h2`
  font-family: 'Roboto', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #efefef;
  line-height: 32px;
  letter-spacing: -0.005em;
  text-wrap: balance;
  margin: 16px 0 20px;

  span {
    color: ${({ theme }) => theme.palette.error};
  }

  @media (max-width: 720px) {
    font-size: 22px;
    line-height: 28px;
  }
`;

export const UnderpaidDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 18px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: #efefef;
`;

export const UnderpaidRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
`;

/** Compare-row label (Expected: / Received:) — DS-2 fg-2 neutral grey. */
export const UnderpaidLabel = styled.span`
  color: #a7a8b2;
`;

/**
 * Compare-row value — DS-2 spec: bull green, weight 700, tabular-nums
 * with slight tracking so digits align column-by-column when expected
 * and received amounts stack.
 */
export const UnderpaidValue = styled.span`
  color: ${({ theme }) => theme.palette.primary};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
`;

/**
 * Underpaid "Remaining stake to pay: X SOL" — DS-2 inline label/value
 * combo in bear color. The numeric span keeps tabular-nums so it never
 * jitters as polling refreshes the remaining amount.
 */
export const UnderpaidRemaining = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.error};
  line-height: 18px;
  margin: 6px 0 8px;

  span {
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }
`;

/** Underpaid hint — DS-2 fg-3 caption beneath the remaining-stake row. */
export const UnderpaidHint = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: #7a7e8c;
  line-height: 1.5;
  max-width: 440px;
  margin: 8px auto 0;
`;

// Action buttons container — gap matches DS Payment flow `actions { gap: 14 }`.
export const ActionButtons = styled.div`
  display: flex;
  gap: 14px;
  width: 100%;
  margin-top: auto;
  padding-top: 24px;
`;

/**
 * Primary action button — extends the DS-compliant ButtonFilled
 * (text #0A0A0A on bull, hover #3FB46A, disabled #535D70/#1F2329, focus
 * ring 4px / 0.25). Local override: stretch to fill the action row.
 */
export const PrimaryButton = styled(ButtonFilled)`
  flex: 1 1 0;
  min-width: 0;
`;

/**
 * Secondary action button — extends the DS-compliant CancelButton
 * (1px #535D70 outline, #EFEFEF text, 700 weight, 50px tall). Local
 * override: stretch to fill the action row.
 */
export const SecondaryButton = styled(CancelButton)`
  flex: 1 1 0;
  min-width: 0;
`;

// Verification state components (Figma 88:299)
export const VerificationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  /* Tightened from 24 → 12 on desktop and 8 on mobile so the verify orb
   * sits closer to the timer pill above. The original spacing assumed
   * a hero illustration above the orb; with the DS-2 orb the extra
   * 24 px just produced a visible dead band on mobile where the Belt
   * already wraps onto two rows. */
  padding: 12px 0 16px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease-out;

  @media (max-width: 720px) {
    padding: 8px 0 16px;
  }
`;

/**
 * VerificationTitle — DS-2 Payment flow / VerifyScreen H1.
 * 26/700, tight tracking. The amount span uses tabular-nums so the
 * skeleton → real-amount transition does not jitter horizontally.
 */
export const VerificationTitle = styled.h2`
  font-family: 'Roboto', sans-serif;
  font-size: 26px;
  font-weight: 700;
  color: #efefef;
  line-height: 32px;
  letter-spacing: -0.005em;
  margin-bottom: 20px;

  span {
    color: ${({ theme }) => theme.palette.primary};
    font-variant-numeric: tabular-nums;
  }
`;

/**
 * VerificationStatusText — secondary line under the title for non-verify
 * states (e.g. "Your game is being created"). DS-2 spec drops this line
 * from 18 to 14, since it sits beside the spinner rather than as a hero.
 */
export const VerificationStatusText = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.white};
  line-height: 18px;
  margin-bottom: 24px;
`;

/**
 * VerificationSubText — DS-2 supporting copy under the title.
 * 13/300/#A7A8B2 sits as a subtle subtitle, with margin-bottom for
 * spacing into the next block (status row). White-space pre-wrap keeps
 * the legacy timeout-mode hard line break working.
 */
export const VerificationSubText = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #a7a8b2;
  line-height: 18px;
  margin-top: 0;
  margin-bottom: 16px;
  white-space: pre-wrap;
  max-width: 460px;
`;

/**
 * Skeleton shimmer used while the stake session is still loading and
 * expectedAmount/currency are not yet known. Same width as a typical
 * "0.05 SOL" amount fragment so the title doesn't reflow when the real
 * value arrives.
 */
export const VerifyAmountSkeleton = styled.span`
  display: inline-block;
  width: 96px;
  height: 22px;
  vertical-align: -3px;
  border-radius: 6px;
  background: linear-gradient(
    90deg,
    rgba(82, 211, 129, 0.10) 0%,
    rgba(82, 211, 129, 0.22) 50%,
    rgba(82, 211, 129, 0.10) 100%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1400ms ease-in-out infinite;
`;

export const Spinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid rgba(82, 211, 129, 0.1);
  border-left-color: ${({ theme }) => theme.palette.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

/**
 * SuccessBadge (formerly SuccessIcon) — DS-2 Detected/equal+over hero.
 * 76×76 solid-bull tile with a 38px check glyph centered. The legacy
 * outer glow was removed per DS-2 ("badge stays flat, no aura"); for
 * an embossed feel the radius keeps a slight 16 px softening.
 */
export const SuccessIcon = styled.div`
  width: 76px;
  height: 76px;
  margin: 4px auto 18px;
  border-radius: 16px;
  background: ${({ theme }) => theme.palette.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: #0a0a0a;

  svg {
    width: 38px;
    height: 38px;
  }
`;

/**
 * VerificationHero — DS-2 Detected/under hero artwork. Sized down from
 * the legacy 320 px hero to 200 px and dimmed (opacity 0.85) so it
 * reads as a supporting illustration beneath the title rather than a
 * full-bleed banner.
 */
export const VerificationHero = styled.div`
  width: 100%;
  max-width: 200px;
  height: 130px;
  margin: 4px auto 18px;
  background-image: url(${heroImage});
  background-size: contain;
  background-position: center;
  background-repeat: no-repeat;
  opacity: 0.85;
`;

/**
 * VerifyOrb — DS-2 Payment flow / VerifyScreen hero.
 * 96×96 disc with a soft bull-green radial bloom and a rotating
 * top-and-right border arc that signals "work in progress" without a
 * heavyweight chart graphic. Pairs with VerifyOrbCore + ShieldGlyph.
 */
const orbSpin = keyframes`
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
`;

export const VerifyOrb = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  margin: 0 auto 20px;
  border-radius: 50%;
  background:
    radial-gradient(
      circle at 30% 30%,
      rgba(82, 211, 129, 0.22) 0%,
      rgba(82, 211, 129, 0.08) 38%,
      rgba(21, 25, 30, 0) 70%
    ),
    #15191e;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: -2px;
    border-radius: 50%;
    border: 2px solid transparent;
    border-top-color: #52d381;
    border-right-color: #52d381;
    box-shadow: 0 0 12px rgba(82, 211, 129, 0.35);
    animation: ${orbSpin} 900ms linear infinite;
    pointer-events: none;
  }
`;

export const VerifyOrbCore = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(82, 211, 129, 0.12);
  border: 1px solid rgba(82, 211, 129, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #52d381;
`;

const verifyDotPulse = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(82, 211, 129, 0.55);
  }
  50% {
    transform: scale(1.15);
    box-shadow: 0 0 0 6px rgba(82, 211, 129, 0);
  }
`;

/**
 * VerifyStatus — DS-2 status line: pulse dot + "Verifying transaction
 * on NETWORK…" with the network name in bull-green. Replaces the legacy
 * standalone "Verifying the transaction..." paragraph + spinner combo.
 */
export const VerifyStatus = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 999px;
  background: rgba(82, 211, 129, 0.06);
  border: 1px solid rgba(82, 211, 129, 0.18);
  margin-top: 8px;

  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #efefef;

  span.network {
    color: #52d381;
    font-weight: 700;
    letter-spacing: 0.02em;
  }
`;

export const VerifyPulseDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #52d381;
  animation: ${verifyDotPulse} 1200ms ease-in-out infinite;
  flex-shrink: 0;
`;

/**
 * DetectedDivider — DS-2 horizontal rule that separates the title /
 * primary message from the compare-rows + delta block on the Detected
 * (over/under) variants. 540 px max-width prevents it from running
 * edge-to-edge on wide cards.
 */
export const OverpaymentDivider = styled.hr`
  width: 100%;
  max-width: 540px;
  border: none;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin: 0 auto 22px;
`;

/**
 * Overpayment lead message — supports a bold em phrase ("more than the
 * required stake") via a child <b>. The bold span is set to fg-1 so it
 * pops out of the surrounding fg-2 grey.
 */
export const OverpaymentMessage = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 13px;
  font-weight: 300;
  color: #a7a8b2;
  line-height: 1.5;
  text-align: center;
  max-width: 480px;
  margin: 0 auto 14px;

  b {
    color: #efefef;
    font-weight: 700;
  }
`;

export const OverpaymentRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  line-height: 18px;
  color: #efefef;
`;

/** Compare-row label (Expected: / Received:) — DS-2 fg-2 grey. */
export const OverpaymentLabel = styled.span`
  color: #a7a8b2;
`;

/** Compare-row value — DS-2 bull/700/tabular-nums + 0.02em tracking. */
export const OverpaymentValue = styled.span`
  color: ${({ theme }) => theme.palette.primary};
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.02em;
`;

/** Delta-block label ("Overpayment:") — DS-2 fg-2 caption above the big number. */
export const OverpaymentAmountLabel = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #a7a8b2;
  line-height: 18px;
  margin-top: 6px;
`;

/**
 * Delta-block value — DS-2 32/700/-0.005em tabular-nums. Drops to 26
 * on mobile so it doesn't dominate the column on phones.
 */
export const OverpaymentAmountValue = styled.span`
  font-family: 'Roboto', sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: ${({ theme }) => theme.palette.primary};
  line-height: 1.1;
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;
  margin-top: 4px;

  @media (max-width: 720px) {
    font-size: 26px;
  }
`;

/** Delta-block hint — DS-2 fg-3 fine-print under the big number. */
export const OverpaymentHint = styled.p`
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: #7a7e8c;
  line-height: 1.5;
  text-align: center;
  max-width: 440px;
  margin: 8px auto 0;
`;

/**
 * Detected/Continue button wrapper — DS-2 spec centres the Continue
 * CTA inside the action row instead of stretching it edge-to-edge,
 * leaving negative space around the primary action.
 */
export const DetectedContinueWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 8px;
`;

/**
 * Detected/Continue button — extends ButtonFilled with a 180 px
 * minimum width so the label always has breathing room without
 * inheriting PrimaryButton's edge-to-edge stretch.
 */
export const DetectedContinueButton = styled(ButtonFilled)`
  min-width: 180px;
  flex: none;
`;

/**
 * BackToPaymentButton — DS-4 .secondary variant from
 * trading-battles-design-system 4/project/preview/components-buttons.html
 * (transparent fill + 1 px #535D70 border + #EFEFEF text + 50 h + 700 / 16).
 * The previous #696969 border belonged to the tertiary-compact spec
 * (38 h / 13 / 500); on a 50 h hero-width back-button the secondary
 * border (#535D70) matches DS spec.
 */
export const BackToPaymentButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  height: 50px;
  padding: 0 24px;
  background: transparent;
  border: 1px solid #535D70;
  border-radius: 12px;
  color: #efefef;
  font-family: 'Roboto', sans-serif;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.04);
    border-color: #7A7E8C;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.palette.primary};
    outline-offset: 2px;
  }
`;
